import { useNavigate } from "react-router-dom";
import {
  Check, X, Loader2, UserPlus, FileText, CalendarPlus, CalendarClock, Trash2, Receipt, Mail, BellRing, ReceiptEuro, PenLine, CircleCheck, CircleAlert, ArrowUpRight, Sparkles,
  Reply, Tag, Truck, Construction, Clock, Banknote, HardHat, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { euro } from "@/lib/format";

const TOOL = {
  crea_contatto: { icon: UserPlus, label: "Nuovo contatto" },
  modifica_contatto: { icon: PenLine, label: "Modifica contatto" },
  crea_preventivo: { icon: FileText, label: "Nuovo preventivo" },
  cambia_stato_preventivo: { icon: FileText, label: "Stato preventivo" },
  crea_appuntamento: { icon: CalendarPlus, label: "Nuovo appuntamento" },
  modifica_appuntamento: { icon: CalendarClock, label: "Modifica appuntamento" },
  elimina_appuntamento: { icon: Trash2, label: "Elimina appuntamento" },
  crea_promemoria: { icon: BellRing, label: "Promemoria" },
  imposta_rate_fattura: { icon: ReceiptEuro, label: "Rate fattura" },
  segna_rata: { icon: ReceiptEuro, label: "Pagamento rata" },
  registra_spesa: { icon: Receipt, label: "Nuova spesa" },
  invia_email: { icon: Mail, label: "Invia email" },
  rispondi_email: { icon: Reply, label: "Rispondi email" },
  modifica_preventivo: { icon: PenLine, label: "Modifica preventivo" },
  aggiungi_voce_listino: { icon: Tag, label: "Nuova voce listino" },
  modifica_voce_listino: { icon: Tag, label: "Modifica voce listino" },
  elimina_voce_listino: { icon: Trash2, label: "Elimina voce listino" },
  crea_ddt: { icon: Truck, label: "Nuovo DDT" },
  crea_cantiere: { icon: Construction, label: "Nuovo cantiere" },
  modifica_cantiere: { icon: Construction, label: "Modifica cantiere" },
  registra_ore: { icon: Clock, label: "Ore lavorate" },
  aggiungi_acconto: { icon: Banknote, label: "Acconto cantiere" },
  crea_operaio: { icon: HardHat, label: "Nuovo operaio" },
  registra_spesa_cantiere: { icon: Receipt, label: "Spesa cantiere" },
  registra_movimento_operaio: { icon: Wallet, label: "Anticipo / rimborso operaio" },
  crea_pagamento: { icon: Banknote, label: "Promemoria pagamento" },
  segna_pagamento: { icon: CircleCheck, label: "Pagamento" },
};

const quoteTotal = (voci = []) => voci.reduce((s, v) => {
  const imp = (Number(v.quantita) || 0) * (Number(v.prezzo) || 0) * (1 - (Number(v.sconto) || 0) / 100);
  return s + imp * (1 + (v.iva ?? 22) / 100);
}, 0);

const Details = ({ a }) => {
  const x = a.args || {};
  if (a.tool === "crea_preventivo") {
    return (
      <ul className="text-xs text-muted-foreground space-y-0.5 mt-2" data-no-translate>
        {(x.voci || []).slice(0, 6).map((v, i) => <li key={i} className="truncate">• {v.quantita} {v.unita || ""} {v.nome} — {euro(v.prezzo)}</li>)}
        {(x.voci || []).length > 6 && <li data-testid={`agent-action-more-${a.id}`}>+ altre {x.voci.length - 6} voci (le vedi tutte aprendo il preventivo)</li>}
        <li className="font-semibold text-foreground pt-1">≈ {euro(quoteTotal(x.voci))} IVA incl.</li>
      </ul>
    );
  }
  if (a.tool === "invia_email") {
    return <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap line-clamp-6" data-no-translate><b>{x.to}</b> · {x.oggetto}{"\n"}{x.testo}</div>;
  }
  if (a.tool === "rispondi_email") {
    return <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap line-clamp-6" data-no-translate>{x.testo}</div>;
  }
  if (a.tool === "crea_ddt" && x.righe?.length) {
    return <ul className="text-xs text-muted-foreground space-y-0.5 mt-2" data-no-translate>{x.righe.slice(0, 6).map((r, i) => <li key={i} className="truncate">• {r.quantita} {r.unita || ""} {r.descrizione}</li>)}</ul>;
  }
  if (["registra_spesa_cantiere", "registra_movimento_operaio"].includes(a.tool) && x.importo != null) {
    return (
      <div className="text-xs text-muted-foreground mt-2" data-no-translate data-testid={`agent-action-amount-${a.id}`}>
        <b className="text-foreground">{euro(x.importo)}</b> · {x.categoria || x.tipo}{x.descrizione || x.note ? ` · ${x.descrizione || x.note}` : ""}{x.data ? ` · ${x.data.split("-").reverse().join("/")}` : ""}
      </div>
    );
  }
  if (["aggiungi_voce_listino", "modifica_voce_listino"].includes(a.tool) && x.prezzo != null) {
    return <div className="text-xs text-muted-foreground mt-2" data-no-translate>{x.nome} — {euro(x.prezzo)}{x.unita ? ` / ${x.unita}` : ""}</div>;
  }
  return null;
};

export const ActionCard = ({ a, busy, onConfirm, onCancel }) => {
  const meta = TOOL[a.tool] || { icon: Sparkles, label: a.tool };
  const Icon = meta.icon;
  const pending = a.status === "pending";
  return (
    <div className={`rounded-xl border p-3.5 bg-card transition-colors ${pending ? "border-primary/40 shadow-sm" : "border-border opacity-80"}`} data-testid={`agent-action-${a.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="w-[18px] h-[18px]" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{meta.label}</div>
          <div className="text-sm font-medium mt-0.5" data-no-translate>{a.riepilogo || "—"}</div>
          <Details a={a} />
        </div>
      </div>
      {pending ? (
        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="ghost" onClick={() => onCancel(a)} disabled={busy} data-testid={`agent-action-cancel-${a.id}`}><X className="w-4 h-4 mr-1" /> Annulla</Button>
          <Button size="sm" onClick={() => onConfirm(a)} disabled={busy} data-testid={`agent-action-confirm-${a.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Conferma</>}
          </Button>
        </div>
      ) : (
        <div className="text-xs mt-2 text-right font-medium text-muted-foreground" data-testid={`agent-action-status-${a.id}`}>
          {a.status === "done" ? "Eseguita" : a.status === "cancelled" ? "Annullata" : "Non riuscita"}
        </div>
      )}
    </div>
  );
};

export const ResultCard = ({ r }) => {
  const navigate = useNavigate();
  return (
    <div className={`rounded-xl border px-3.5 py-3 flex items-center gap-3 ${r.ok ? "border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/30" : "border-rose-300/60 bg-rose-50 dark:bg-rose-950/30"}`} data-testid="agent-result">
      {r.ok ? <CircleCheck className="w-5 h-5 text-emerald-600 shrink-0" /> : <CircleAlert className="w-5 h-5 text-rose-600 shrink-0" />}
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-semibold">{r.ok ? "Fatto" : "Non riuscito"}</div>
        <div className="text-muted-foreground truncate" data-no-translate>{r.ok ? r.label : r.error}</div>
      </div>
      {r.ok && r.link && (
        <Button size="sm" variant="outline" onClick={() => navigate(r.link)} data-testid="agent-result-open"><ArrowUpRight className="w-4 h-4 mr-1" /> Apri</Button>
      )}
    </div>
  );
};

export default ActionCard;
